import {useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode, type RefObject} from 'react';
import {Download, Pause, Play} from 'lucide-react';
import {useIntl} from 'react-intl';
import PlaybackSpeedButton from '../../../../components/AudioPlayer/PlaybackSpeedButton';
import {formatTime} from '../../../../utilis/timeUtilis';
import {insightsMessages as m} from '../insightsMessages';
import {focusRing, iconButton, lucide} from '../insightsUi';
import {logPlaybackSeek} from '../insightsLog';
import {usePlayback} from './PlaybackContext';
import {resamplePeaks, speechEnvelope} from './waveformPeaks';
import {useWaveformPeaks} from './useWaveformPeaks';

const BAR_SLOT_PX = 5;
const DEFAULT_BARS = 72;
const KEY_STEP_SECONDS = 5;

const useBarCount = (ref: RefObject<HTMLElement>): number => {
    const [bars, setBars] = useState(DEFAULT_BARS);
    useEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === 'undefined') return;
        const update = (width: number) => {
            if (width > 0) setBars(Math.max(24, Math.floor(width / BAR_SLOT_PX)));
        };
        update(element.getBoundingClientRect().width);
        const observer = new ResizeObserver((entries) => entries.forEach((entry) => update(entry.contentRect.width)));
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return bars;
};

interface InsightsPlayerProps {
    speech: readonly {startTime: number; endTime: number}[];
    downloadName: string;
    lanes?: ReactNode;
}

export const InsightsPlayer = ({speech, downloadName, lanes}: InsightsPlayerProps) => {
    const intl = useIntl();
    const playback = usePlayback();
    const {currentTime, duration, playing, src} = playback;
    const waveRef = useRef<HTMLDivElement>(null);
    const bars = useBarCount(waveRef);
    const peaks = useWaveformPeaks(src, duration);
    const dragging = useRef(false);

    const levels = useMemo(
        () => (peaks ? resamplePeaks(peaks, bars) : speechEnvelope(speech, duration, bars)),
        [peaks, bars, speech, duration],
    );
    const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
    const current = formatTime(currentTime);
    const total = formatTime(duration);

    const seekToPointer = (event: PointerEvent<HTMLDivElement>) => {
        const rect = waveRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0 || duration <= 0) return;
        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        playback.seek(ratio * duration);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const moves: Record<string, number> = {
            ArrowLeft: currentTime - KEY_STEP_SECONDS,
            ArrowDown: currentTime - KEY_STEP_SECONDS,
            ArrowRight: currentTime + KEY_STEP_SECONDS,
            ArrowUp: currentTime + KEY_STEP_SECONDS,
            PageDown: currentTime - KEY_STEP_SECONDS * 6,
            PageUp: currentTime + KEY_STEP_SECONDS * 6,
            Home: 0,
            End: duration,
        };
        if (!(event.key in moves)) return;
        event.preventDefault();
        event.stopPropagation();
        playback.seek(moves[event.key]);
        logPlaybackSeek('keyboard');
    };

    return (
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
            <button
                type="button"
                onClick={playback.toggle}
                aria-label={intl.formatMessage(playing ? m.pause : m.play)}
                className={`inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-control border-0 bg-brand p-0 text-white hover:bg-brand-hover ${focusRing}`}
            >
                {playing ? <Pause size={16} aria-hidden="true" className={lucide} fill="currentColor"/> : <Play size={16} aria-hidden="true" className={lucide} fill="currentColor"/>}
            </button>
            <div
                ref={waveRef}
                role="slider"
                tabIndex={0}
                aria-label={intl.formatMessage(m.seekLabel)}
                aria-valuemin={0}
                aria-valuemax={Math.round(duration)}
                aria-valuenow={Math.round(currentTime)}
                aria-valuetext={intl.formatMessage(m.seekValue, {current, total})}
                data-insights-slider=""
                onKeyDown={onKeyDown}
                onPointerDown={(event) => {
                    dragging.current = true;
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                    seekToPointer(event);
                }}
                onPointerMove={(event) => {
                    if (dragging.current) seekToPointer(event);
                }}
                onPointerUp={(event) => {
                    if (!dragging.current) return;
                    dragging.current = false;
                    event.currentTarget.releasePointerCapture?.(event.pointerId);
                    logPlaybackSeek('waveform');
                }}
                className={`flex h-9 cursor-pointer touch-none items-center justify-between rounded-chip ${focusRing}`}
            >
                {levels.map((level, index) => {
                    const played = (index + 0.5) / levels.length <= progress;
                    return (
                        <span
                            key={index}
                            aria-hidden="true"
                            className={`w-[3px] shrink-0 rounded-[2px] ${played ? 'bg-brand-focus' : 'bg-fill-subtle'}`}
                            style={{height: `${Math.round(Math.max(0.08, level) * 100)}%`}}
                        />
                    );
                })}
            </div>
            <div className="flex items-center gap-1">
                <span className="min-w-[88px] text-right text-caption tabular-nums text-ink">
                    {current} / {total}
                </span>
                <PlaybackSpeedButton rate={playback.rate} onClick={playback.cycleRate} shape="tint" className="ml-1"/>
                {src && (
                    <a
                        href={src}
                        download={downloadName}
                        aria-label={intl.formatMessage(m.download)}
                        title={intl.formatMessage(m.download)}
                        className={iconButton}
                    >
                        <Download size={18} aria-hidden="true" className={lucide}/>
                    </a>
                )}
            </div>
            {lanes && (
                <>
                    {lanes}
                    <span aria-hidden="true"/>
                </>
            )}
        </div>
    );
};
